import _ from 'lodash';
import { LifecycleStatus, Post, PostFilters } from '@/type';
import eventBus from '@/messaging/event-bus';
import {
  PostSaved,
  PostEscalated,
  PostResolved,
  PostTrashed,
  PostRestored, PostRemovedFromTimeline, PostMovedToTimeline,
} from '@/messaging/events';
import api from '../API';
import { PostTranscript } from './types';

export const statusText = [
  'Unreviewed',
  'Saved',
  'Escalated',
  'Resolved',
];

export function getStatusText(status: number): string {
  return statusText[status];
}

export const status: { [key: string]: number } = {
  UNREVIEWED: 0,
  SAVED: 1,
  ESCALATED: 2,
  RESOLVED: 3,
};

export function isArchived(post: Post): boolean {
  return post.lifecycleStatus === LifecycleStatus.ARCHIVED;
}

export function isTrashed(post: Post): boolean {
  return post.lifecycleStatus === LifecycleStatus.TRASHED;
}

export function allStatuses(): number[] {
  return [
    status.UNREVIEWED,
    status.SAVED,
    status.ESCALATED,
    status.RESOLVED,
  ];
}

export function isUnreviewed(postStatus: number) {
  return (postStatus === status.UNREVIEWED);
}

export function isSaved(postStatus: number) {
  return (postStatus === status.SAVED);
}

export function isEscalated(postStatus: number) {
  return (postStatus === status.ESCALATED);
}

export function isResolved(postStatus: number) {
  return (postStatus === status.RESOLVED);
}

export function isOnTimeline(post: Post) {
  return (post.movedToTimelineAt !== null);
}

export function isFlagged(post: Post) {
  return (post.flaggedAt !== null);
}

const TYPE_PHOTO_ALBUM = 2;

export function isPhotoAlbum(type: number): boolean {
  return (type === TYPE_PHOTO_ALBUM);
}

export function matches(p: Post, filters: PostFilters): boolean {
  let keep = filters.statuses.includes(p.status);
  if (_.isBoolean(filters.isTrashed) && (p.isTrashed !== filters.isTrashed)) {
    keep = false;
  }
  if (
    (filters.isOnTimeline === true && !isOnTimeline(p))
    || (filters.isOnTimeline === false && isOnTimeline(p))
  ) {
    keep = false;
  }
  if (filters.isFlagged && p.flaggedAt === null) {
    keep = false;
  }
  if (filters.isFlagged === false && p.flaggedAt !== null) {
    keep = false;
  }
  if (filters.lifecycleStatuses && filters.lifecycleStatuses.length > 0
    && !filters.lifecycleStatuses.includes(p.lifecycleStatus!)) {
    keep = false;
  }
  return keep;
}

export function save(id: number): Promise<Post> {
  return api
    .put(`posts/${id}/save`, { moveToTimeline: true })
    .then((response) => {
      const post = (response.data as Post);
      eventBus.$emit(PostSaved.eventName, new PostSaved(post));
      return post;
    });
}

export function escalate(
  post: Post,
  recipientIds: number[],
  message: string | null,
): Promise<Post> {
  return api
    .put(`posts/${post.id}/escalate`, { recipientIds, message })
    .then((response) => {
      const p = (response.data as Post);
      eventBus.$emit(PostEscalated.eventName, new PostEscalated(p, post));
      return p;
    });
}

export function resolve(id: number, timeline = false): Promise<Post> {
  return api
    .put(`posts/${id}/resolve`, { moveToTimeline: timeline })
    .then((response) => {
      const post = (response.data as Post);
      eventBus.$emit(PostResolved.eventName, new PostResolved(post));
      if (timeline) {
        eventBus.$emit(PostMovedToTimeline.eventName, new PostMovedToTimeline(post));
      }
      return post;
    });
}

export function trash(id: number): Promise<Post> {
  return api
    .put(`posts/${id}/trash`)
    .then((response) => {
      const post = (response.data as Post);
      eventBus.$emit(PostTrashed.eventName, new PostTrashed(post));
      return post;
    });
}

export function restore(id: number): Promise<Post> {
  return api
    .put(`posts/${id}/restore`)
    .then((response) => {
      const post = (response.data as Post);
      eventBus.$emit(PostRestored.eventName, new PostRestored(post));
      return post;
    });
}

export function moveToTimeline(id: number): Promise<Post> {
  return api
    .put(`posts/${id}/move-to-timeline`)
    .then((response) => {
      const post = (response.data as Post);
      eventBus.$emit(PostMovedToTimeline.eventName, new PostMovedToTimeline(post));
      return post;
    });
}

export function removeFromTimeline(id: number): Promise<Post> {
  return api
    .put(`posts/${id}/remove-from-timeline`)
    .then((response) => {
      const post = (response.data as Post);
      eventBus.$emit(PostRemovedFromTimeline.eventName, new PostRemovedFromTimeline(post));
      return post;
    });
}

export function transcribe(id: number): Promise<PostTranscript> {
  return api
    .get(`posts/${id}/media/transcript`)
    .then(response => response.data);
}
